/**
 * Upcoming Deadlines Card
 *
 * List view showing upcoming due dates for projects and tasks.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar, FolderKanban, CheckSquare } from 'lucide-react';
import type { TimelineItem } from '../types';

interface UpcomingDeadlinesCardProps {
  data?: TimelineItem[];
  loading?: boolean;
}

const TZ = 'America/Montreal';

function toMontrealDateString(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const todayStr = toMontrealDateString(now);
  const dateStr = toMontrealDateString(date);

  if (dateStr === todayStr) return 'Today';

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateStr === toMontrealDateString(tomorrow)) return 'Tomorrow';

  const weekFromNow = new Date(now);
  weekFromNow.setDate(weekFromNow.getDate() + 7);
  if (date <= weekFromNow) {
    return date.toLocaleDateString('en-US', { weekday: 'long', timeZone: TZ });
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: TZ });
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'confirmed':
    case 'todo':
      return 'default';
    case 'scheduled':
    case 'in_progress':
      return 'secondary';
    case 'blocked':
    case 'roadblock':
      return 'destructive';
    case 'invoiced':
      return 'outline';
    default:
      return 'outline';
  }
}

export function UpcomingDeadlinesCard({ data, loading }: UpcomingDeadlinesCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasData = data && data.length > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Upcoming Deadlines
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <ScrollArea className="h-[220px]">
            <div className="space-y-3 pr-4">
              {data.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="mt-0.5">
                    {item.type === 'project' ? (
                      <FolderKanban className="h-4 w-4 text-blue-500" />
                    ) : (
                      <CheckSquare className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{item.title}</span>
                      <Badge variant={getStatusBadgeVariant(item.status)} className="text-xs">
                        {item.status.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(item.due_date)}
                      </span>
                      {item.assignee_name && (
                        <>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {item.assignee_name}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
            No upcoming deadlines
          </div>
        )}
      </CardContent>
    </Card>
  );
}
